import { GoogleGenAI } from "@google/genai";


export async function generateCartoonAvatar(imageSrcs, customStyle, apiKey, model = "gemini-2.5-flash-image") {
  try {
    if (!apiKey) throw new Error("API Key is missing.");
    const cleanKey = apiKey.trim().replace(/[\u2018\u2019\u201C\u201D]/g, ""); // Remove smart quotes if any
    const ai = new GoogleGenAI({ apiKey: cleanKey });

    // Ensure imageSrcs is an array
    const images = Array.isArray(imageSrcs) ? imageSrcs : [imageSrcs];

    const defaultStyle = "3D isometric render, c4d, blender style, cute stylized character, white background, only person visible, facing forward, volumetric lighting, soft clay texture, matte finish, vibrant pastel colors, disney pixar style, whimsical.";
    const styleToUse = customStyle && customStyle.trim() !== "" ? customStyle : defaultStyle;

    // Prepare prompt parts
    const prompt = [
      { text: `Create a combined cartoon composition based on the person(s) in the provided photo(s). ${styleToUse}` },
    ];

    // Add each image to the prompt
    for (const img of images) {
        // format: "data:image/jpeg;base64,/9j/..."
        const [header, base64Data] = img.split(',');
        const mimeMatch = header.match(/:(.*?);/);
        const mimeType = mimeMatch ? mimeMatch[1] : 'image/jpeg';
        
        prompt.push({
            inlineData: {
                mimeType: mimeType,
                data: base64Data,
            },
        });
    }

    // 3. Call the API
    const response = await ai.models.generateContent({
      model: model,
      contents: prompt,
    });

    // 4. Handle response to find the generated image
    let cartoonUrl = null;
    
    // Check if response has candidates and parts
    const parts = response.candidates?.[0]?.content?.parts;
    
    if (parts) {
      for (const part of parts) {
        if (part.inlineData) {
          // Found an image! Convert back to Data URL for display
          // Defaulting to image/png if mimeType is missing in response, though it should be there.
          const resMimeType = part.inlineData.mimeType || 'image/png';
          cartoonUrl = `data:${resMimeType};base64,${part.inlineData.data}`;
          break;
        }
      }
    }

    if (!cartoonUrl) {
       console.warn("No image found in response:", response);
       // If no image, maybe there's text explaining why?
       const textPart = parts?.find(p => p.text);
       if (textPart) console.log("Text response:", textPart.text);
       
       throw new Error("No image generated by the model.");
    }

    return { 
      success: true, 
      cartoonUrl, 
      usageMetadata: response.usageMetadata 
    };

  } catch (error) {
    console.error("Error generating avatar:", error);
    // Rethrow or return null/error object depending on how App.js handles it.
    // App.js expects { cartoonUrl: ... } or handles error?
    // Looking at App.js: 
    // const result = await generateCartoonAvatar(image);
    // setCartoon(result.cartoonUrl);
    // So we should return a structure or throw. 
    // Previously we swallowed error and returned mock. Now we should probably let it fail or handle gracefully.
    // Let's return a null cartoonUrl so the UI can handle it (or show error state).
    throw error;
  }
}

export async function analyzeImageStyle(imageSrc, apiKey, model = "gemini-2.5-flash") {
  try {
    if (!apiKey) throw new Error("API Key is missing.");
    const cleanKey = apiKey.trim().replace(/[\u2018\u2019\u201C\u201D]/g, ""); // Remove smart quotes
    const ai = new GoogleGenAI({ apiKey: cleanKey });

    // 1. Parse the input imageSrc
    const [header, base64Data] = imageSrc.split(',');
    const mimeMatch = header.match(/:(.*?);/);
    const mimeType = mimeMatch ? mimeMatch[1] : 'image/jpeg';

    const prompt = [
      { text: "Analyze this image and provide a detailed prompt style description that could be used to generate a similar image. Focus on artistic style, lighting, texture, color palette, and composition. Provide the output as a concise comma-separated list of keywords and phrases." },
      {
        inlineData: {
          mimeType: mimeType,
          data: base64Data,
        },
      },
    ];

    // 2. Call the API using a model that supports image-to-text
    const response = await ai.models.generateContent({
      model: model,
      contents: prompt,
    });

    // 3. Extract text response
    const parts = response.candidates?.[0]?.content?.parts;
    const textPart = parts?.find(p => p.text);
    
    if (!textPart) {
       throw new Error("No text description generated by the model.");
    }

    return { 
      success: true, 
      styleDescription: textPart.text,
      usageMetadata: response.usageMetadata
    };

  } catch (error) {
    console.error("Error analyzing image style:", error);
    throw error;
  }
}
